import {
	CalendarDate,
	CalendarDateTime,
	fromDate,
	getLocalTimeZone,
} from "@internationalized/date";
import cx from "classnames";
import { parseISO } from "date-fns";
import { type FC, useMemo, useState } from "react";

import { CloseIcon } from "@moment/icons";
import { MomentButton } from "@moment/ui";

import { DatePicker } from "../../DatePicker";
import { type TableCellValueCommonProps } from "./types";

export interface TableCellValueDateProps extends TableCellValueCommonProps {
	type: "Date" | "Date time";
	renderAsRowDetail?: boolean;
	label?: string;
}

export const TableCellValueDate: FC<TableCellValueDateProps> = ({
	type,
	value: inputValue,
	defaultValue: defaultInputValue,
	onChange,
	isEditable,
	renderAsRowDetail,
	label,
}) => {
	const [open, setOpen] = useState(false);
	const shouldDisplayClearButton = isEditable && open;

	const value = inputValue ?? defaultInputValue;
	const calendarDate = useMemo(() => {
		try {
			const date = value instanceof Date ? value : parseISO(value);
			const zonedDate = fromDate(date, getLocalTimeZone());
			return type === "Date"
				? new CalendarDate(zonedDate.year, zonedDate.month, zonedDate.day)
				: new CalendarDateTime(zonedDate.year, zonedDate.month, zonedDate.day);
		} catch (e) {
			return undefined;
		}
	}, [value]);

	return (
		<div className="relative flex w-full">
			<div className="mr-2">
				<DatePicker
					// IMPORTANT: Setting `key` allows us to clear out datepicker state when user
					// clears it. Un-setting the state seems like it's not supported formally which
					// is exceptionally strange.
					key={value}
					value={calendarDate}
					type="embedded"
					dialogTrigger="click"
					hideTriggerButton
					onOpenChange={setOpen}
					onChange={(calendarDate) => onChange?.(calendarDate.toDate(getLocalTimeZone()))}
					isReadOnly={!isEditable}
					aria-label={label}
					withPortal={renderAsRowDetail ? false : true}
					constantWidthDateSegments={false}
				/>
			</div>
			<div
				className={cx(
					"flex",
					"items-center",

					{
						//
						// IMPORTANT: Hidden lets `onClick` fire fully! If you conditionally render
						// this element if `shouldDisplayClearButton` is false, the button gets
						// destroyed before the `onClick` can fully fire!
						//
						// ACCESSIBILITY NOTES: This button is not "accessible" (e.g., you cannot
						// use tab to navigate to it), but there are a few big reasons why we do it
						// this way for now.
						//
						//   1. Clearing out a selected date is *NOT* supported by the react-aria
						//      datepicker hooks, as far as we can tell, and it is very hard to
						//      implement this well ourselves, and it is very easy to screw up with
						//      disastrous consequences.
						//
						//      For example, react-aria hooks are very sensitive to
						//      on-click-outside-popover and focus trapping, and if you try to mess
						//      with them it can make the component unusable, e.g., to
						//      screenreaders.
						//
						//      Additionally, if react-aria does NOT implement this I am guessing
						//      that they either have a good reason, or this is not a big enough
						//      deal that it poses a serious obstacle to accessibility in an
						//      enterprise setting.
						//
						//   2. Our lack of tab-navigability is also the behavior of Coda and
						//      Notion. Notion is whatever but I (Alex) know people on the Coda
						//      team, and they care a LOT about accessibility, so I am guessing that
						//      they have a good reason for doing it this way, especially
						//      considering react-aria also has no facilities for this.
						//
						"hidden": !shouldDisplayClearButton,

						// Right-justify x button, negative margin so that it lines up with the
						// chevron icon on the righthand side of the column header dropdown button.
						"-mr-1 ml-auto": !renderAsRowDetail,
					}
				)}
			>
				<MomentButton
					level="tertiary"
					icon={CloseIcon}
					size="xs"
					label="Clear date"
					onClick={() => onChange?.(undefined)}
				/>
			</div>
		</div>
	);
};
